import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {AnalyticsVisualizationErrorBarType as ErrorBarType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ERROR_BAR_TYPE_TO_READABLE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {StringValueDropdownSelector} from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {AggregationAndErrorBarParameters} from 'insights/analytics-visualizations/configs/VisualizationConfigEditorTypes';
import Tooltip from 'ui-library/bs/Tooltip';
import WarningIcon from 'ui-library/icons/WarningIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export interface ErrorBarConfigEditorProps<ChartParameters extends AggregationAndErrorBarParameters> {
  warning?: string | null;
  parameters: ChartParameters;
  onUpdateParameters: (update: Partial<ChartParameters>) => void;
}

/**
 * Settings to configure error bars. To prevent invalid combinations of error bar and point aggregation
 * settings, the dropdown to set errorBarType is disabled if aggregateType is null.
 */
export default function ErrorBarConfigEditor<ChartParameters extends AggregationAndErrorBarParameters>({
  warning = null,
  parameters,
  onUpdateParameters,
}: ErrorBarConfigEditorProps<ChartParameters>): JSX.Element {
  return (
    <div
      className="u-input-spacing-top"
      {...getTestProps({component: 'ErrorBarConfigEditor', element: 'root'})}
    >
      <FieldLabel
        label={
          <>
            Error bars
            {warning && (
              <Tooltip title={warning}>
                <span aria-label={warning}>
                  <WarningIcon className="u-tiny-spacing-left" />
                </span>
              </Tooltip>
            )}
          </>
        }
      />

      <StringValueDropdownSelector
        allowNull={true}
        errorMessage={
          parameters.aggregationType == null
            ? 'Error bars can only be calculated for aggregated points'
            : null
        }
        onChange={(newErrorBarType) => {
          onUpdateParameters({...parameters, errorBarType: newErrorBarType});
        }}
        searchable={false}
        selectedValue={parameters.errorBarType}
        values={[ErrorBarType.STANDARD_DEVIATION, ErrorBarType.STANDARD_ERROR]}
        valueToReadable={(errorBarType) => ERROR_BAR_TYPE_TO_READABLE[errorBarType]}
      />
    </div>
  );
}
